import React, { useState, useEffect } from 'react';
import { Settings, Save, TestTube, CheckCircle, AlertCircle, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useGoogleAuth } from '../../GoogleAuthProvider';
import { getAccessToken } from '../../auth/google';
import { useMemo } from 'react';

interface SheetConfig {
  id: string;
  name: string;
  spreadsheetId: string;
  sheetName: string;
  isConnected: boolean;
  lastTested: string | null;
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { isAuthed, connect } = useGoogleAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  const [sheetConfigs, setSheetConfigs] = useState<SheetConfig[]>([
    {
      id: 'dashboard',
      name: 'Dashboard Stats',
      spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ',
      sheetName: 'Dashboard Stats',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'pipeline-leads',
      name: 'Pipeline',
      spreadsheetId: '',
      sheetName: 'Pipeline',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'lead-requests',
      name: 'Lead Requests',
      spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ',
      sheetName: 'Leads_Requests',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'pre-qualified',
      name: 'Pre-Qualified Leads',
      spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ',
      sheetName: 'Staging_TBQ',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'qualified',
      name: 'Qualified Ready Leads',
      spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ',
      sheetName: 'MAIN_Ready_Leads',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'call-leads',
      name: 'Call Agent',
      spreadsheetId: '1ycMtZ44fHhkO7NUi2QJtGDMXJAuu9CWPl1Fv2BH55Dw',
      sheetName: 'Leads_Calls',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'whatsapp-leads',
      name: 'WhatsApp Agent',
      spreadsheetId: '18QQjzM1LGR0AzIyicEPTqo3qCiSA4KyHeMTN4wD-y_E',
      sheetName: 'Leads_WA',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'email-leads',
      name: 'Email Agent',
      spreadsheetId: '13lv1kfIBbvTp21PH_Za3AXwv-JBj5uW4jHAVujZK-_A',
      sheetName: 'Leads_Emails',
      isConnected: false,
      lastTested: null
    },
    {
      id: 'meetings',
      name: 'Meetings',
      spreadsheetId: '',
      sheetName: 'Meetings',
      isConnected: false,
      lastTested: null
    }
  ]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [isResettingData, setIsResettingData] = useState(false);

  useEffect(() => {
    // Load saved configurations from localStorage
    const savedConfigs = localStorage.getItem('googleSheetsConfigs');
    if (savedConfigs) {
      setSheetConfigs(JSON.parse(savedConfigs));
    }
  }, []);

  const handleAuthenticate = async () => {
    setIsAuthenticating(true);
    try {
      connect();
      // Wait a moment for the authentication to complete
      setTimeout(() => {
        if (user?.isDemo) {
          setSaveMessage({ type: 'success', text: 'Successfully Authenticated!' });
        } else {
          setSaveMessage({ type: 'success', text: 'Successfully authenticated with Google Sheets!' });
        }
        setTimeout(() => setSaveMessage(null), 3000);
      }, 2000);
    } catch (error) {
      console.error('Authentication error in component:', error);
      setSaveMessage({ 
        type: 'error', 
        text: user?.isDemo ? 'Authentication error. Please try again.' : 'Authentication error. Check browser console and Google Cloud Console configuration.' 
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleConfigChange = (id: string, field: keyof SheetConfig, value: string) => {
    setSheetConfigs(prev => prev.map(config => 
      config.id === id ? { ...config, [field]: value, isConnected: false, lastTested: null } : config
    ));
  };

  const testConnection = async (config: SheetConfig) => {
    if (!isAuthed) {
      setSaveMessage({ type: 'error', text: 'Please authenticate with Google first.' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    if (!config.spreadsheetId || !config.sheetName) {
      setSaveMessage({ type: 'error', text: 'Please fill in both Spreadsheet ID and Sheet Name.' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    try {
      const { googleSheetsService } = await import('../../services/googleSheetsService');
      
      // Test the connection by trying to read from the sheet
      await googleSheetsService.readSheet(config.spreadsheetId, config.sheetName, 'A1:A1');
      
      setSheetConfigs(prev => prev.map(c => 
        c.id === config.id 
          ? { ...c, isConnected: true, lastTested: new Date().toISOString() }
          : c
      ));
      
      setSaveMessage({ type: 'success', text: `Connection to ${config.name} successful!` });
    } catch (error) {
      setSheetConfigs(prev => prev.map(c => 
        c.id === config.id 
          ? { ...c, isConnected: false, lastTested: new Date().toISOString() }
          : c
      ));
      
      setSaveMessage({ type: 'error', text: `Failed to connect to ${config.name}. Check your configuration.` });
    }
    
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const saveConfigurations = () => {
    setIsSaving(true);
    
    setTimeout(async () => {
      // Save to localStorage
      localStorage.setItem('googleSheetsConfigs', JSON.stringify(sheetConfigs));
      
      // Update the service with new configurations
      const { googleSheetsService } = await import('../../services/googleSheetsService');
      googleSheetsService.updateConfigurations(sheetConfigs);
      
      setIsSaving(false);
      setSaveMessage({ type: 'success', text: 'Configurations saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    }, 1000);
  };

  const handleResetDemoData = async () => {
    const confirmMessage = user?.isDemo 
      ? 'Are you sure you want to reset the demo data? This action cannot be undone!'
      : 'Are you sure you want to reset demo data?\n\n' +
        'This will permanently delete specific rows from the following sheets:\n' +
        '• Leads Requests (rows 6+)\n' +
        '• Pre-Qualified Leads (rows 3+)\n' +
        '• Qualified Ready Leads (rows 5+)\n' +
        '• Call Agent (rows 4+)\n' +
        '• WhatsApp Agent (rows 4+)\n' +
        '• Email Agent (rows 4+)\n' +
        '• Meetings (rows 4+)\n\n' +
        'This action cannot be undone!';

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    setIsResettingData(true);
    try {
      const { googleSheetsService } = await import('../../services/googleSheetsService');
      
      await googleSheetsService.resetDemoData();
      setSaveMessage({
        type: 'success',
        text: user?.isDemo 
          ? 'Demo data has been successfully reset!'
          : 'Demo data has been successfully reset! All specified rows have been deleted.'
      });
    } catch (error) {
      console.error('Failed to reset demo data:', error);
      setSaveMessage({
        type: 'error',
        text: user?.isDemo 
          ? 'Failed to reset demo data. Please try again.'
          : 'Failed to reset demo data. Please check the console for details and try again.'
      });
    } finally {
      setIsResettingData(false);
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  const addCustomSheet = () => {
    const newConfig: SheetConfig = {
      id: `custom-${Date.now()}`,
      name: 'Custom Sheet',
      spreadsheetId: '',
      sheetName: '',
      isConnected: false,
      lastTested: null
    };
    setSheetConfigs(prev => [...prev, newConfig]);
  };

  const removeCustomSheet = (id: string) => {
    setSheetConfigs(prev => prev.filter(config => config.id !== id));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Authentication Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {user?.isDemo ? 'Google Authentication' : 'Google Authentication'}
        </h2>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isAuthed ? (
              <>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-green-700 font-medium">
                  {user?.isDemo ? 'Connected' : 'Connected to Google Sheets'}
                </span>
              </>
            ) : (
              <>
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-red-700 font-medium">
                  {user?.isDemo ? 'Not connected' : 'Not connected to Google Sheets'}
                </span>
              </>
            )}
          </div>
          
          <button
            onClick={handleAuthenticate}
            disabled={isAuthenticating}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
          >
            {isAuthenticating ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <CheckCircle size={16} />
            )}
            <span>
              {isAuthenticating 
                ? 'Connecting...' 
                : isAuthed 
                  ? 'Reconnect to Google' 
                  : 'Connect to Google'
              }
            </span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Demo Data Management</h3>
            <p className="text-gray-600 text-sm">Reset demo data to start fresh with clean datasets</p>
          </div>
          <button
            onClick={handleResetDemoData}
            disabled={isResettingData || !isAuthed}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors"
          >
            {isResettingData ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <RefreshCw size={20} />
            )}
            <span>{isResettingData ? 'Resetting...' : 'Reset Demo Data'}</span>
          </button>
        </div>
      </div>

      {/* Sheet Configurations - Only show for non-demo users */}
      {!user?.isDemo && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Sheet Configurations</h2>
            <button
              onClick={addCustomSheet}
              className="flex items-center space-x-2 px-3 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <Plus size={16} />
              <span>Add Custom Sheet</span>
            </button>
          </div>

          <div className="space-y-6">
            {sheetConfigs.map((config) => (
              <div key={config.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="font-medium text-gray-900">{config.name}</h3>
                    {config.isConnected ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle size={12} className="mr-1" />
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <AlertCircle size={12} className="mr-1" />
                        Not Connected
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => testConnection(config)}
                      disabled={!isAuthed}
                      className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      <TestTube size={14} />
                      <span>Test</span>
                    </button>
                    
                    {config.id.startsWith('custom-') && (
                      <button
                        onClick={() => removeCustomSheet(config.id)}
                        className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={config.name}
                      onChange={(e) => handleConfigChange(config.id, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Call Agent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Spreadsheet ID
                    </label>
                    <input
                      type="text"
                      value={config.spreadsheetId}
                      onChange={(e) => handleConfigChange(config.id, 'spreadsheetId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sheet Name
                    </label>
                    <input
                      type="text"
                      value={config.sheetName}
                      onChange={(e) => handleConfigChange(config.id, 'sheetName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Leads"
                    />
                  </div>
                </div>

                {config.lastTested && (
                  <div className="mt-2 text-xs text-gray-500">
                    Last tested: {new Date(config.lastTested).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Button - Only show for non-demo users */}
      {!user?.isDemo && (
        <div className="flex items-center justify-between space-x-4">
          <div className="text-sm text-gray-500">
            Changes are saved locally and will be applied immediately.
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={saveConfigurations}
              disabled={isSaving}
              className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save size={20} />
              )}
              <span>{isSaving ? 'Saving...' : 'Save Configurations'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {saveMessage && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center space-x-2">
            {saveMessage.type === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            <span className="font-medium">{saveMessage.text}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;